import { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { Layout } from '../../components/Layout'
import { hasAnyRole } from '../../utils/permissions'
import { supabase } from '../../services/supabase'
import { Plus, Edit, Trash2, X, Save, UserPlus, CheckSquare, Square, Trash, Tag } from 'lucide-react'

const AdminSchools = () => {
  const { userProfile, loading: authLoading } = useAuth()
  const [schools, setSchools] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showTeacherModal, setShowTeacherModal] = useState(false)
  const [editingSchool, setEditingSchool] = useState(null)
  const [selectedSchool, setSelectedSchool] = useState(null)
  const [formData, setFormData] = useState({
    okul_adi: '',
    selected_categories: [],
    ilgili_kisi: '',
    ilgili_kisi_telefon: '',
  })
  const [teacherForm, setTeacherForm] = useState({
    teacher_name: '',
    teacher_phone: '',
  })
  const [schoolTeachers, setSchoolTeachers] = useState([])
  const [schoolCategories, setSchoolCategories] = useState([])
  const [selectedSchools, setSelectedSchools] = useState([])
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false)
  const [bulkCategoryForm, setBulkCategoryForm] = useState({
    selected_categories: [],
  })

  useEffect(() => {
    if (userProfile) {
      fetchSchools()
      fetchSchoolCategories()
    }
  }, [userProfile])

  const fetchSchoolCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('school_categories')
        .select('*')
        .eq('is_active', true)
        .order('display_name')

      if (error) throw error
      setSchoolCategories(data || [])
    } catch (error) {
      console.error('Okul kategorileri yüklenemedi:', error)
    }
  }

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Yükleniyor...</div>
      </div>
    )
  }

  if (!userProfile || !hasAnyRole(userProfile, ['super_admin', 'admin'])) {
    return <Navigate to="/unauthorized" replace />
  }

  const fetchSchools = async () => {
    try {
      const { data, error } = await supabase
        .from('schools')
        .select(`
          *,
          categories:school_category_relations(category:school_categories(*))
        `)
        .order('okul_adi')

      if (error) throw error
      setSchools(data || [])
    } catch (error) {
      console.error('Okullar yüklenemedi:', error)
      alert('Veri yüklenirken hata oluştu: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchSchoolTeachers = async (schoolId) => {
    try {
      const { data, error } = await supabase
        .from('school_teachers')
        .select('*')
        .eq('school_id', schoolId)
        .order('teacher_name')

      if (error) throw error
      setSchoolTeachers(data || [])
    } catch (error) {
      console.error('Öğretmenler yüklenemedi:', error)
    }
  }

  const handleCreate = () => {
    setEditingSchool(null)
    setFormData({
      okul_adi: '',
      selected_categories: [],
      ilgili_kisi: '',
      ilgili_kisi_telefon: '',
    })
    setShowModal(true)
  }

  const handleEdit = async (school) => {
    setEditingSchool(school)
    
    // Okulun kategorilerini yükle
    try {
      const { data: categories, error } = await supabase
        .from('school_category_relations')
        .select('category_id')
        .eq('school_id', school.id)

      if (error) throw error

      setFormData({
        okul_adi: school.okul_adi,
        selected_categories: categories?.map(c => c.category_id) || [],
        ilgili_kisi: school.ilgili_kisi || '',
        ilgili_kisi_telefon: school.ilgili_kisi_telefon || '',
      })
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error)
      // Hata durumunda eski yöntemle devam et
      setFormData({
        okul_adi: school.okul_adi,
        selected_categories: [],
        ilgili_kisi: school.ilgili_kisi || '',
        ilgili_kisi_telefon: school.ilgili_kisi_telefon || '',
      })
    }
    
    setShowModal(true)
  }

  const handleDelete = async (schoolId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Bu okulu silmek istediğinize emin misiniz?')) return

    try {
      const { error } = await supabase.from('schools').delete().eq('id', schoolId)
      if (error) throw error
      setSelectedSchools([])
      await fetchSchools()
    } catch (error) {
      alert('Silme işlemi başarısız: ' + error.message)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedSchools.length === 0) {
      alert('Lütfen silmek için en az bir okul seçin')
      return
    }

    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`${selectedSchools.length} okulu silmek istediğinize emin misiniz?`)) return

    try {
      const { error } = await supabase
        .from('schools')
        .delete()
        .in('id', selectedSchools)

      if (error) throw error
      setSelectedSchools([])
      await fetchSchools()
      alert(`${selectedSchools.length} okul başarıyla silindi`)
    } catch (error) {
      alert('Toplu silme işlemi başarısız: ' + error.message)
    }
  }

  const toggleSelectAll = () => {
    if (selectedSchools.length === schools.length) {
      setSelectedSchools([])
    } else {
      setSelectedSchools(schools.map(s => s.id))
    }
  }

  const toggleSelectSchool = (schoolId) => {
    if (selectedSchools.includes(schoolId)) {
      setSelectedSchools(selectedSchools.filter(id => id !== schoolId))
    } else {
      setSelectedSchools([...selectedSchools, schoolId])
    }
  }

  const handleBulkCategoryChange = () => {
    if (selectedSchools.length === 0) {
      alert('Lütfen kategori değiştirmek için en az bir okul seçin')
      return
    }
    setBulkCategoryForm({ selected_categories: [] })
    setShowBulkCategoryModal(true)
  }

  const handleBulkCategorySubmit = async (e) => {
    e.preventDefault()

    if (bulkCategoryForm.selected_categories.length === 0) {
      alert('Lütfen en az bir kategori seçin')
      return
    }

    try {
      // Her seçili okul için kategorileri güncelle
      for (const schoolId of selectedSchools) {
        // Mevcut kategorileri sil
        await supabase
          .from('school_category_relations')
          .delete()
          .eq('school_id', schoolId)

        // Yeni kategorileri ekle
        const categoryRelations = bulkCategoryForm.selected_categories.map((categoryId) => ({
          school_id: schoolId,
          category_id: categoryId,
        }))

        const { error: categoryError } = await supabase
          .from('school_category_relations')
          .insert(categoryRelations)

        if (categoryError) throw categoryError

        // Geriye dönük uyumluluk için ilk kategoriyi okul_kategorisi olarak kaydet
        const firstCategory = schoolCategories.find(c => c.id === bulkCategoryForm.selected_categories[0])
        if (firstCategory) {
          await supabase
            .from('schools')
            .update({ okul_kategorisi: firstCategory.name })
            .eq('id', schoolId)
        }
      }

      setShowBulkCategoryModal(false)
      setSelectedSchools([])
      setBulkCategoryForm({ selected_categories: [] })
      await fetchSchools()
      alert(`${selectedSchools.length} okulun kategorileri başarıyla güncellendi`)
    } catch (error) {
      alert('Toplu kategori değiştirme işlemi başarısız: ' + error.message)
    }
  }

  const handleManageTeachers = async (school) => {
    setSelectedSchool(school)
    await fetchSchoolTeachers(school.id)
    setShowTeacherModal(true)
  }

  const handleAddTeacher = async (e) => {
    e.preventDefault()
    try {
      const { error } = await supabase.from('school_teachers').insert({
        school_id: selectedSchool.id,
        teacher_name: teacherForm.teacher_name,
        teacher_phone: teacherForm.teacher_phone,
      })

      if (error) throw error

      setTeacherForm({ teacher_name: '', teacher_phone: '' })
      await fetchSchoolTeachers(selectedSchool.id)
    } catch (error) {
      alert('Öğretmen eklenirken hata oluştu: ' + error.message)
    }
  }

  const handleDeleteTeacher = async (teacherId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Bu öğretmeni silmek istediğinize emin misiniz?')) return

    try {
      const { error } = await supabase
        .from('school_teachers')
        .delete()
        .eq('id', teacherId)

      if (error) throw error
      await fetchSchoolTeachers(selectedSchool.id)
    } catch (error) {
      alert('Silme işlemi başarısız: ' + error.message)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      // Kategorileri formData'dan çıkar
      const { selected_categories, ...schoolData } = formData
      
      // En az bir kategori seçilmeli
      if (selected_categories.length === 0) {
        alert('Lütfen en az bir kategori seçin')
        return
      }
      
      // Geriye dönük uyumluluk için ilk kategoriyi okul_kategorisi olarak kaydet
      const firstCategory = schoolCategories.find(c => c.id === selected_categories[0])
      if (firstCategory) {
        schoolData.okul_kategorisi = firstCategory.name
      }

      let schoolId

      if (editingSchool) {
        const { error } = await supabase
          .from('schools')
          .update(schoolData)
          .eq('id', editingSchool.id)

        if (error) throw error
        schoolId = editingSchool.id

        // Mevcut kategorileri sil
        await supabase
          .from('school_category_relations')
          .delete()
          .eq('school_id', schoolId)
      } else {
        const { data, error } = await supabase
          .from('schools')
          .insert(schoolData)
          .select()
          .single()

        if (error) throw error
        schoolId = data.id
      }

      // Kategorileri ekle
      console.log('Kaydedilecek okul kategorileri:', selected_categories)
      const categoryRelations = selected_categories.map((categoryId) => ({
        school_id: schoolId,
        category_id: categoryId,
      }))

      console.log('Okul kategori ilişkileri:', categoryRelations)

      const { data: insertedCategories, error: categoryError } = await supabase
        .from('school_category_relations')
        .insert(categoryRelations)
        .select()

      if (categoryError) {
        console.error('Okul kategori kaydetme hatası:', categoryError)
        throw categoryError
      }
      
      console.log('Okul kategorileri başarıyla kaydedildi:', insertedCategories)

      setShowModal(false)
      await fetchSchools()
    } catch (error) {
      alert('İşlem başarısız: ' + error.message)
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Yükleniyor...</div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Okul Yönetimi</h1>
          <p className="text-gray-600 mt-1">Okul bilgilerini yönetin</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedSchools.length > 0 && (
            <>
              <button
                onClick={handleBulkCategoryChange}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 shadow-md transition-all duration-200"
              >
                <Tag size={18} />
                <span>Kategori Değiştir ({selectedSchools.length})</span>
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 shadow-md transition-all duration-200"
              >
                <Trash size={18} />
                <span>Seçili Olanları Sil ({selectedSchools.length})</span>
              </button>
            </>
          )}
          <button
            onClick={handleCreate}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg transition-all duration-200"
          >
            <Plus size={20} />
            <span>Yeni Okul</span>
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                <button
                  onClick={toggleSelectAll}
                  className="flex items-center justify-center"
                  title={selectedSchools.length === schools.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                >
                  {selectedSchools.length === schools.length && schools.length > 0 ? (
                    <CheckSquare size={20} className="text-blue-600" />
                  ) : (
                    <Square size={20} className="text-gray-400" />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Okul Adı
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Kategori
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                İlgili Kişi
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Telefon
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                İşlemler
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {schools.map((school) => (
              <tr key={school.id} className={selectedSchools.includes(school.id) ? 'bg-blue-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedSchools.includes(school.id)}
                    onChange={() => toggleSelectSchool(school.id)}
                    className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {school.okul_adi}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {school.categories && school.categories.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {school.categories.map((rel, idx) => (
                        rel.category && (
                          <span
                            key={idx}
                            className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800"
                          >
                            {rel.category.display_name}
                          </span>
                        )
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400">
                      {school.okul_kategorisi || '-'}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {school.ilgili_kisi || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {school.ilgili_kisi_telefon || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleManageTeachers(school)}
                    className="text-green-600 hover:text-green-900 mr-4"
                    title="Öğretmen Yönet"
                  >
                    <UserPlus size={18} />
                  </button>
                  <button
                    onClick={() => handleEdit(school)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(school.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Okul Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingSchool ? 'Okul Düzenle' : 'Yeni Okul'}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Okul Adı *
                </label>
                <input
                  type="text"
                  required
                  value={formData.okul_adi}
                  onChange={(e) => setFormData({ ...formData, okul_adi: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Okul Kategorileri * (Çoklu Seçim)
                </label>
                <div className="border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto">
                  {schoolCategories.length === 0 ? (
                    <p className="text-sm text-gray-500">Kategori bulunamadı</p>
                  ) : (
                    <div className="space-y-2">
                      {schoolCategories.map((category) => {
                        const isChecked = formData.selected_categories.includes(category.id)
                        return (
                          <label
                            key={category.id}
                            className="flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const newCategories = e.target.checked
                                  ? [...formData.selected_categories, category.id]
                                  : formData.selected_categories.filter((id) => id !== category.id)
                                
                                console.log('Okul kategori seçimi:', {
                                  category: category.display_name,
                                  checked: e.target.checked,
                                  newCategories,
                                  oldCategories: formData.selected_categories
                                })
                                
                                setFormData({
                                  ...formData,
                                  selected_categories: newCategories,
                                })
                              }}
                              className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                            />
                            <span className="text-sm text-gray-900">{category.display_name}</span>
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>
                {formData.selected_categories.length > 0 && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-700">
                      <strong>{formData.selected_categories.length}</strong> kategori seçildi
                    </p>
                  </div>
                )}
                {formData.selected_categories.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">En az bir kategori seçmelisiniz</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  İlgili Kişi
                </label>
                <input
                  type="text"
                  value={formData.ilgili_kisi}
                  onChange={(e) => setFormData({ ...formData, ilgili_kisi: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  İlgili Kişi Telefon
                </label>
                <input
                  type="tel"
                  value={formData.ilgili_kisi_telefon}
                  onChange={(e) =>
                    setFormData({ ...formData, ilgili_kisi_telefon: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <Save size={18} />
                  <span>Kaydet</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Öğretmen Modal */}
      {showTeacherModal && selectedSchool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {selectedSchool.okul_adi} - Öğretmen Yönetimi
              </h2>
              <button onClick={() => setShowTeacherModal(false)}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleAddTeacher} className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold mb-3">Yeni Öğretmen Ekle</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ad Soyad *
                  </label>
                  <input
                    type="text"
                    required
                    value={teacherForm.teacher_name}
                    onChange={(e) =>
                      setTeacherForm({ ...teacherForm, teacher_name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    required
                    value={teacherForm.teacher_phone}
                    onChange={(e) =>
                      setTeacherForm({ ...teacherForm, teacher_phone: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                  />
                </div>
              </div>
              <button
                type="submit"
                className="mt-3 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Öğretmen Ekle
              </button>
            </form>

            <div>
              <h3 className="font-semibold mb-3">Mevcut Öğretmenler</h3>
              <div className="space-y-2">
                {schoolTeachers.map((teacher) => (
                  <div
                    key={teacher.id}
                    className="flex justify-between items-center p-3 bg-white border rounded-lg"
                  >
                    <div>
                      <div className="font-medium">{teacher.teacher_name}</div>
                      <div className="text-sm text-gray-500">{teacher.teacher_phone}</div>
                    </div>
                    <button
                      onClick={() => handleDeleteTeacher(teacher.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
                {schoolTeachers.length === 0 && (
                  <p className="text-gray-500 text-center py-4">Henüz öğretmen eklenmemiş</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toplu Kategori Değiştirme Modal */}
      {showBulkCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Tag size={24} />
                Toplu Kategori Değiştir ({selectedSchools.length} Okul)
              </h2>
              <button onClick={() => setShowBulkCategoryModal(false)}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleBulkCategorySubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Okul Kategorileri * (Çoklu Seçim)
                </label>
                <div className="border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto">
                  {schoolCategories.length === 0 ? (
                    <p className="text-sm text-gray-500">Kategori bulunamadı</p>
                  ) : (
                    <div className="space-y-2">
                      {schoolCategories.map((category) => {
                        const isChecked = bulkCategoryForm.selected_categories.includes(category.id)
                        return (
                          <label
                            key={category.id}
                            className="flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const newCategories = e.target.checked
                                  ? [...bulkCategoryForm.selected_categories, category.id]
                                  : bulkCategoryForm.selected_categories.filter((id) => id !== category.id)
                                
                                setBulkCategoryForm({
                                  ...bulkCategoryForm,
                                  selected_categories: newCategories,
                                })
                              }}
                              className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                            />
                            <span className="text-sm text-gray-900">{category.display_name}</span>
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>
                {bulkCategoryForm.selected_categories.length > 0 && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-700">
                      <strong>{bulkCategoryForm.selected_categories.length}</strong> kategori seçildi
                    </p>
                  </div>
                )}
                {bulkCategoryForm.selected_categories.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">En az bir kategori seçmelisiniz</p>
                )}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Uyarı:</strong> Seçili {selectedSchools.length} okulun mevcut kategorileri silinecek ve yeni kategoriler atanacaktır.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowBulkCategoryModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  <Save size={18} />
                  <span>Kategorileri Güncelle</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  )
}

export default AdminSchools



